import { Box, Divider, Stack, styled } from '@mui/material';
import {
    ConfirmationDialog,
    PoolButton,
    PoolFormattedMessage,
    PoolMenu,
    PoolMenuItem,
    PoolTable,
    PoolTableColumn,
    PoolTableRow,
} from '@synergy/components';
import { EditIcon, TrashIcon } from '@synergy/theme';
import { FormattedMessage } from 'react-intl';
import {
    SystemNotification,
    SystemNotificationType,
    deleteNotification,
    getSystemNotifications,
    notificationsSelector,
    postNotification,
    updateNotification,
    useAppDispatch,
} from '@synergy/admin/app-settings/data-access';
import { noop } from '@synergy/utils';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';

import { NotificationsModal } from './notifications-modal';

const Container = styled(Box)(({ theme }) => ({
    margin: theme.spacing(4, 8),
    flex: 1,
    maxWidth: 830,
}));

const StyledTable = styled(PoolTable)(() => ({
    '& > thead > tr > th': {
        backgroundColor: '#FAFAFA',
    },
}));

export const NotificationSettings = () => {
    const dispatch = useAppDispatch();
    const notifications = useSelector(notificationsSelector);
    const [notification, setNotification] = useState<SystemNotification | null>(null);
    const [deleteModalData, setDeleteModalData] = useState<SystemNotification | null>(null);

    useEffect(() => {
        dispatch(getSystemNotifications());
    }, [dispatch]);

    const onNotificationEdit = (notification: SystemNotification): void => {
        setNotification(notification);
    };

    const openDeleteModal = (notification: SystemNotification): void => {
        setDeleteModalData(notification);
    };

    const onNotificationDelete = (): void => {
        if (deleteModalData) {
            dispatch(deleteNotification(deleteModalData.id));
            setDeleteModalData(null);
        }
    };

    const onNotificationAdd = () => {
        setNotification({ id: new Date().getTime() * -1, type: SystemNotificationType.ImportFinished, email: '' });
    };
    const handleNotificationSubmit = (notification: SystemNotification) => {
        const editedItem = notifications.find(n => n.id === notification.id);
        if (editedItem) {
            dispatch(updateNotification(notification));
        } else {
            dispatch(postNotification(notification));
        }

        setNotification(null);
    };

    const notificationColumns: PoolTableColumn[] = [
        {
            id: 'type',
            label: <FormattedMessage id="app.label.type" />,
            sortable: false,
            width: '275px',
        },
        { id: 'email', label: <FormattedMessage id="app.table.column.emailTo" />, sortable: false },
        { id: 'menu', sortable: false, width: '44px' },
    ];

    const getNotificationRows = (): PoolTableRow[] => {
        const rows = notifications.map((notification, index) => {
            return {
                id: index,
                type: <FormattedMessage id={`app.enum.systemNotificationType.${notification.type}`} />,
                email: notification.email,
                menu: (
                    <PoolMenu menuPosition="right">
                        <PoolMenuItem
                            icon={<EditIcon />}
                            title={<FormattedMessage id="app.common.edit" />}
                            onClick={() => onNotificationEdit(notification)}
                        />
                        <PoolMenuItem
                            icon={<TrashIcon />}
                            title={<FormattedMessage id="app.common.delete" />}
                            onClick={() => openDeleteModal(notification)}
                        />
                    </PoolMenu>
                ),
            };
        });

        return rows;
    };

    return (
        <Container>
            {notifications.length > 0 ? (
                <Stack>
                    <StyledTable
                        columns={notificationColumns}
                        rows={getNotificationRows()}
                        enableHover={false}
                        onRowClicked={noop}
                    />
                    <Divider sx={{ mb: 2 }} />
                    <PoolButton
                        styleType="thirdLevel"
                        startIcon={<AddCircleOutline color="primary" />}
                        onClick={onNotificationAdd}
                        sx={{ width: 230, ml: 1 }}
                    >
                        <FormattedMessage id="app.settings.notification.addNotification" />
                    </PoolButton>
                </Stack>
            ) : (
                <Stack alignItems="left" sx={{ gap: 2 }}>
                    <FormattedMessage id="app.settings.notification.empty.text" />
                    <PoolButton
                        styleType="firstLevel"
                        variant="contained"
                        onClick={onNotificationAdd}
                        sx={{ width: 271 }}
                    >
                        <FormattedMessage id="app.settings.notification.addNotification" />
                    </PoolButton>
                </Stack>
            )}
            <NotificationsModal
                notification={notification}
                onClose={() => setNotification(null)}
                onNotificationSubmit={handleNotificationSubmit}
            />
            <ConfirmationDialog
                isVisible={!!deleteModalData}
                title={<FormattedMessage id="app.settings.notification.deleteModal.title" />}
                action={<FormattedMessage id="app.common.delete" />}
                onConfirm={onNotificationDelete}
                onCancel={() => setDeleteModalData(null)}
            >
                <PoolFormattedMessage
                    id="app.settings.notification.deleteModal.text"
                    values={{ email: deleteModalData?.email }}
                />
            </ConfirmationDialog>
        </Container>
    );
};
